import React, { useCallback } from 'react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';

import { ANALYTICS_SOURCE, SHARE_CONTEXT_TOOL_ACTION_SUBJECT_ID } from '../../analytics.ts';
import { useTwgAnalyticsEvents } from '../../useTwgAnalyticsEvents.ts';

import { DEFAULT_SHARE_CONTEXT_LEVEL } from './shareContextPrompt.ts';
import type {
	ShareContextTarget,
	ShareContextTargetId,
	ShareContextTool,
} from './shareContextTools.tsx';

const i18n = defineMessages({
	desktopTarget: {
		id: 'byline-connections.share-context-menu.target-desktop',
		defaultMessage: '{toolName} Desktop',
		description:
			'Item in the "Share with agent" menu that opens this page’s context in an AI tool’s desktop app rather than its website, where toolName is the tool, such as Claude or ChatGPT.',
	},
	webTarget: {
		id: 'byline-connections.share-context-menu.target-web',
		defaultMessage: '{toolName} Web',
		description:
			'Item in the "Share with agent" menu that opens this page’s context on an AI tool’s website rather than in its desktop app, where toolName is the tool, such as Claude or ChatGPT.',
	},
	newWindowTarget: {
		id: 'byline-connections.share-context-menu.target-new-window-label',
		defaultMessage: '{targetLabel} (opens new window)',
		description:
			'Accessible label for a "Share with agent" menu item that opens in a new browser tab, where targetLabel is the item’s visible text such as Claude Web.',
	},
});

const TARGET_LABELS: Record<ShareContextTargetId, MessageDescriptor> = {
	desktop: i18n.desktopTarget,
	web: i18n.webTarget,
};

export type ShareContextTargetItemProps = {
	tool: ShareContextTool;
	target: ShareContextTarget;
	prompt: string;
};

/**
 * One deep link in the share-context menu, reporting its own hand-off. A tool with a single
 * target is named alone; one with several names the target too, so "Claude Desktop" and
 * "Claude Web" stay distinguishable.
 */
export function ShareContextTargetItem({
	tool,
	target,
	prompt,
}: ShareContextTargetItemProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const { id: toolId, name, Logo, targets } = tool;
	const { id, buildHref, opensInNewTab } = target;

	const handleClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: SHARE_CONTEXT_TOOL_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
				attributes: { tool: toolId, target: id, contextLevel: DEFAULT_SHARE_CONTEXT_LEVEL },
			},
		}).fire();
	}, [createAnalyticsEvent, toolId, id]);

	const label = targets.length > 1 ? formatMessage(TARGET_LABELS[id], { toolName: name }) : name;

	return (
		<DropdownItem
			// `LinkItem` adds no new-window hint of its own, the way `Anchor` does.
			aria-label={
				opensInNewTab ? formatMessage(i18n.newWindowTarget, { targetLabel: label }) : undefined
			}
			elemBefore={<Logo size="xsmall" />}
			href={buildHref(encodeURIComponent(prompt))}
			onClick={handleClick}
			rel={opensInNewTab ? 'noopener noreferrer' : undefined}
			target={opensInNewTab ? '_blank' : undefined}
			testId={`share-context-menu-tool-${toolId}-${id}`}
		>
			{label}
		</DropdownItem>
	);
}
